<script setup lang="ts">
import { Thumbs, Pagination } from "swiper/modules";
import { SwiperSlide } from "swiper/vue";

import type { FavoriteItem } from "~/types";

interface Props {
  list: {
    // 画像URL
    image: string;
    // 画像コメント
    comment: string;
    // 施工例、プラン番号
    cd: string;
    // 何枚目の画像か
    num: number;
  }[];
  slideFavorite?: boolean;
  initialSlide?: number;
  subTitle?: string;
}
const props = withDefaults(defineProps<Props>(), {
  list: () => [],
  initialSlide: 0,
  slideFavorite: false,
  subTitle: "",
});

// お気に入り追加用 現在表示しているスライド
const selectSlide = defineModel<FavoriteItem>();

const thumbsSwiper = ref(null);

function setThumbsSwiper(swiper: any) {
  thumbsSwiper.value = swiper;
}

const isShow = ref(false);
function onInit() {
  isShow.value = true;
}

// swiperオブジェクトのactiveIndexを使う
function onSlide(e: any) {
  const item = props.list.find((i) => i.num == e.activeIndex + 1);

  if (item) {
    selectSlide.value = {
      cd: item.cd,
      num: item.num,
      comment: item.comment,
    };
  }
}
</script>

<template>
  <div class="c-gallery" :style="{ visibility: isShow ? 'visible' : 'hidden' }">
    <SwiperBase
      class="swiper-main relative mx-auto aspect-square overflow-hidden w-full max-w-[500px] mb-8"
      :slides-per-view="1"
      :loop="true"
      :thumbs="{ swiper: thumbsSwiper }"
      :modules="[Pagination, Thumbs]"
      :space-between="10"
      :pagination="{
        clickable: true,
      }"
      :centered-slides="true"
      :initial-slide="initialSlide"
      @init="onInit"
      @slide-change="onSlide"
    >
      <SwiperSlide v-for="(item, index) in list" :key="item.image">
        <div
          class="h-[75%] bg-gray-50 flex flex-col gap-4 justify-center relative w-full"
        >
          <ImageEl
            class="w-full h-full object-contain"
            provider="customimgproxy"
            :src="item.image"
            :alt="item.comment"
            width="500"
            height="375"
            sizes="sm:375px md:500px"
            :loading="
              initialSlide != undefined
                ? index != initialSlide
                  ? 'lazy'
                  : 'eager'
                : index > 0
                  ? 'lazy'
                  : 'eager'
            "
            :preload="
              initialSlide != undefined ? index == initialSlide : index == 0
            "
            format="webp"
          />
          <span
            class="absolute right-2 bottom-2 text-sm bg-gray-700 text-white rounded-lg px-1 py-0.5"
            >{{ index + 1 }}/{{ list?.length }}</span
          >
          <FavoriteToggleButton
            v-if="slideFavorite"
            class="absolute top-2 right-2 drop-shadow"
            :item="{
              cd: item.cd,
              num: item.num,
              comment: item.comment,
            }"
          />
        </div>
        <p class="h-[25%] p-4 rounded">
          {{ item.comment }}
        </p>
      </SwiperSlide>
    </SwiperBase>
    <h2 v-if="subTitle" class="text-center text-base tablet:text-lg mb-4">
      {{ subTitle }}
    </h2>
    <SwiperBase
      class="swiper-thumbs relative aspect-[4/1] overflow-hidden mx-4"
      :space-between="10"
      :slides-per-view="2.5"
      :breakpoints="{
        '600': {
          slidesPerView: 3.5,
        },
      }"
      :watch-slides-progress="true"
      @swiper="setThumbsSwiper"
    >
      <SwiperSlide
        v-for="(item, index) in list"
        :key="item.image"
        class="subslide_item border-2 border-gray-100 cursor-pointer"
      >
        <ImageEl
          provider="customimgproxy"
          class="w-full h-full object-contain hover:brightness-90"
          :src="item.image"
          :alt="item.comment"
          width="500"
          height="375"
          sizes="100px md:350px"
          :loading="index > 2 ? 'lazy' : 'eager'"
          format="webp"
        />
      </SwiperSlide>
    </SwiperBase>
  </div>
</template>

<style scoped>
.subslide_item.swiper-slide-thumb-active {
  @apply border-famitei-green;
}
</style>
